import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import type { VideoListItem } from '@ayub/shared'
import { formatNumber, formatSom } from '@ayub/shared'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateUz } from '@/lib/relative-date'
import { cn } from '@/lib/utils'
import { VIDEOS_PAGE_SIZE } from './api'
import { VideoKanbanColumnEmptyState, VideosNoResultsEmptyState } from './EmptyStates'
import { VideosErrorBanner, VideosRetryState } from './ErrorStates'
import { Pagination } from './Pagination'
import { VideoStageBadge } from './VideoStageBadge'
import { videoCardStatus } from './urgency'

const columnHelper = createColumnHelper<VideoListItem>()

const columns = [
  columnHelper.accessor((row) => row.blogger.fullName, {
    id: 'fullName',
    header: 'Bloger',
    cell: (info) => <span className="font-medium text-app-text">{info.getValue()}</span>,
  }),
  columnHelper.accessor((row) => row.blogger.instagramUsername, {
    id: 'instagramUsername',
    header: 'Instagram',
    cell: (info) => <span className="text-app-hint">@{info.getValue()}</span>,
  }),
  columnHelper.accessor('stage', {
    id: 'stage',
    header: 'Bosqich',
    cell: (info) => <VideoStageBadge stage={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'status',
    header: 'Holat',
    cell: (info) => {
      const status = videoCardStatus(info.row.original)
      return <span className={cn('text-xs font-medium', status.toneClass)}>{status.text}</span>
    },
  }),
  columnHelper.accessor('views', {
    id: 'views',
    header: 'Prosmotr',
    cell: (info) => {
      const value = info.getValue()
      return <span className="tabular">{value !== null ? formatNumber(value) : '—'}</span>
    },
  }),
  columnHelper.accessor('amount', {
    id: 'amount',
    header: 'Summa',
    cell: (info) => {
      const value = info.getValue()
      return <span className="tabular">{value !== null ? formatSom(value) : '—'}</span>
    },
  }),
]

interface VideoListViewProps {
  videos: VideoListItem[]
  total: number
  page: number
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  searchTerm: string
  hasActiveFilters: boolean
  /** "Bugun tekshirish" tez filtri bo'sh natija bergan holatda quvonchli xabar. */
  celebratory?: boolean
  onPageChange: (page: number) => void
  onSelectVideo: (id: number) => void
  onRetry: () => void
  onClearFilters: () => void
}

/**
 * List ko'rinishi — desktopda TanStack Table jadval, `< 640px`da kartochkalar ro'yxati
 * (`docs/04-ui-ux.md` "Jadval (List ko'rinish)": "Mobil: jadval kartochkalar ro'yxatiga
 * aylanadi"), `components/bloggers/BloggerListView.tsx` bilan bir xil naqsh.
 */
export function VideoListView({
  videos,
  total,
  page,
  isLoading,
  isFetching,
  error,
  searchTerm,
  hasActiveFilters,
  celebratory,
  onPageChange,
  onSelectVideo,
  onRetry,
  onClearFilters,
}: VideoListViewProps) {
  const table = useReactTable({
    data: videos,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (isLoading) {
    return <VideoListSkeleton />
  }

  if (error && videos.length === 0) {
    return <VideosRetryState error={error} onRetry={onRetry} />
  }

  if (total === 0) {
    if (celebratory) {
      return (
        <div className="py-6">
          <VideoKanbanColumnEmptyState celebratory />
        </div>
      )
    }
    return (
      <VideosNoResultsEmptyState
        searchTerm={searchTerm}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {error && <VideosErrorBanner error={error} isFetching={isFetching} onRetry={onRetry} />}

      {/* Desktop / tablet: jadval */}
      <div className="hidden overflow-x-auto rounded-card border border-app-border sm:block">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-app-card">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                tabIndex={0}
                role="button"
                aria-label={`Video #${row.original.id} — batafsil ko'rish`}
                onClick={() => onSelectVideo(row.original.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelectVideo(row.original.id)
                  }
                }}
                className="h-12 cursor-pointer border-b border-app-border last:border-b-0 hover:bg-app-card focus-visible:bg-app-card"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobil: kartochkalar ro'yxati */}
      <div className="flex flex-col gap-2 sm:hidden">
        {videos.map((video) => (
          <VideoMobileCard key={video.id} video={video} onSelect={onSelectVideo} />
        ))}
      </div>

      <Pagination
        page={page}
        pageSize={VIDEOS_PAGE_SIZE}
        total={total}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function VideoMobileCard({
  video,
  onSelect,
}: {
  video: VideoListItem
  onSelect: (id: number) => void
}) {
  const status = videoCardStatus(video)
  return (
    <button
      type="button"
      onClick={() => onSelect(video.id)}
      className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-3 text-left transition-colors duration-150 ease-out hover:bg-brand-subtle"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col">
          <span className="font-medium text-app-text">{video.blogger.fullName}</span>
          <span className="text-xs text-app-hint">@{video.blogger.instagramUsername}</span>
        </div>
        <VideoStageBadge stage={video.stage} className="shrink-0" />
      </div>
      <span className={cn('text-xs font-medium', status.toneClass)}>{status.text}</span>
      <dl className="grid grid-cols-3 gap-2 text-xs">
        <MobileStat
          label="Prosmotr"
          value={video.views !== null ? formatNumber(video.views) : '—'}
        />
        <MobileStat label="Summa" value={video.amount !== null ? formatSom(video.amount) : '—'} />
        <MobileStat
          label="Sana"
          value={video.postedAt ? formatDateUz(video.postedAt) : formatDateUz(video.createdAt)}
        />
      </dl>
    </button>
  )
}

function MobileStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-app-hint">{label}</dt>
      <dd className="tabular font-medium text-app-text">{value}</dd>
    </div>
  )
}

function VideoListSkeleton() {
  return (
    // `aria-busy` — skrinrider uchun: skelet paytida sahifa jim turmasin, "yuklanmoqda"
    // holati e'lon qilinsin, aks holda kontent birdan paydo bo'lganda foydalanuvchi
    // hech qanday signal olmaydi.
    <div className="flex flex-col gap-3" aria-busy="true">
      {/* Desktop skeleton */}
      <div className="hidden overflow-hidden rounded-card border border-app-border sm:block">
        <div className="flex h-10 items-center gap-4 border-b border-app-border bg-app-card px-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={`head-${index}`} className={cn('h-3', index === 0 ? 'w-32' : 'w-16')} />
          ))}
        </div>
        {Array.from({ length: 8 }, (_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className="flex h-12 items-center gap-4 border-b border-app-border px-3 last:border-b-0"
          >
            {Array.from({ length: 6 }, (_, colIndex) => (
              <Skeleton
                key={`cell-${rowIndex}-${colIndex}`}
                className={cn('h-3', colIndex === 0 ? 'w-32' : 'w-16')}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Mobil skeleton */}
      <div className="flex flex-col gap-2 sm:hidden">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-[100px]" />
        ))}
      </div>
    </div>
  )
}
